import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { HERO_IMG } from "../data/products";
import { useLang } from "../i18n";
import { EASE, Magnetic, Micro } from "./Reveal";

export default function Hero({ start }) {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });

  const onMouseMove = (e) => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 18);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 12);
  };

  const line = (text, delay, innerClass = "") => (
    <span className="block overflow-hidden">
      <motion.span
        className={`block will-change-transform ${innerClass}`}
        initial={{ y: "115%" }}
        animate={start ? { y: "0%" } : { y: "115%" }}
        transition={{ duration: 1.15, delay, ease: EASE }}
      >
        {text}
      </motion.span>
    </span>
  );

  const scrollToProducts = () => {
    const el = document.querySelector("#products");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -60 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] overflow-hidden flex flex-col justify-end"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <motion.div className="w-full h-full" style={{ x: sx, y: sy }}>
          <motion.img
            src={HERO_IMG}
            alt="INT–EXT designed luxury living room interior"
            className="w-full h-[115%] object-cover object-[62%_center] md:object-center brightness-[0.85]"
            initial={{ scale: 1.15, opacity: 0 }}
            animate={start ? { scale: 1.05, opacity: 0.92 } : {}}
            transition={{ duration: 1.8, ease: EASE }}
          />
        </motion.div>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 md:via-ink/30 to-ink/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/50 via-transparent to-transparent md:hidden" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-espresso/30" />

      <motion.div style={{ opacity: fade }} className="relative z-10 px-5 md:px-12 pb-10 md:pb-12">
        <h1 className="font-display uppercase leading-[0.96] tracking-[-0.01em] text-[clamp(2.4rem,10vw,7.5rem)]">
          {line(t("hero.l1"), 0.35)}
          {line(t("hero.l2"), 0.47)}
          {line(t("hero.l3"), 0.59, "text-outline-gold")}
        </h1>

        <div className="mt-7 md:mt-8 flex flex-col md:flex-row md:flex-wrap md:items-end md:justify-between gap-7 md:gap-8">
          <motion.p
            className="max-w-sm md:max-w-md text-sm md:text-base text-cream/60 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 w-full md:w-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
          >
            <Magnetic className="w-full sm:w-auto">
              <button
                data-testid="hero-explore-button"
                data-cursor="link"
                onClick={scrollToProducts}
                className="group inline-flex w-full sm:w-auto items-center justify-between sm:justify-start gap-3 bg-gold text-ink px-7 py-4 text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-goldbright transition-colors duration-300"
              >
                {t("hero.explore")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </Magnetic>
            <Link
              to="/catalogue"
              data-testid="hero-catalogue-link"
              data-cursor="link"
              className="group self-start sm:self-auto inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase text-cream/80 hover:text-gold transition-colors border-b border-white/25 hover:border-gold pb-1"
            >
              {t("hero.catalogue")} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="mt-9 md:mt-10 pt-4 border-t border-white/10 flex items-center justify-between gap-4"
          initial={{ opacity: 0 }}
          animate={start ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1.15 }}
        >
          <Micro className="text-[9px] whitespace-nowrap">{t("hero.stripL")}</Micro>
          <div className="hidden md:flex items-center gap-3">
            <Micro>{t("hero.scroll")}</Micro>
            <span className="relative w-px h-8 bg-white/15 overflow-hidden">
              <span className="absolute inset-x-0 top-0 h-3 bg-gold gold-glow" />
            </span>
          </div>
          <Micro className="text-[9px] whitespace-nowrap">{t("hero.stripR")}</Micro>
        </motion.div>
      </motion.div>
    </section>
  );
}
